// invest_FE/src/components/verification/TwoFactorAuth.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { auth } from '../../firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import AuthenticatorSetup from './AuthenticatorSetup';
import AuthenticatorReset from './AuthenticatorReset';
import './TwoFactorAuth.css';

const TwoFactorAuth = ({ userId, phoneNumber, emailMasked, hasAuthenticator, setupToken, onLoginSuccess }) => {
  const { send2faCode, verify2faCode } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('select_method'); // 'select_method', 'enter_code', 'setup_authenticator', 'reset_authenticator'
  const [selectedMethod, setSelectedMethod] = useState(null); // 'app', 'email', 'sms'
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [notification, setNotification] = useState('');
  const [isError, setIsError] = useState(false);
  const [localHasAuthenticator, setLocalHasAuthenticator] = useState(hasAuthenticator);
  const recaptchaVerifierRef = useRef(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  useEffect(() => {
    setLocalHasAuthenticator(hasAuthenticator);
  }, [hasAuthenticator]);

  useEffect(() => {
    // This effect will run when the component unmounts to clean up the verifier.
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures it runs only on mount and unmount.

  useEffect(() => {
    // This effect manages the countdown timer for the resend button.
    if (resendCooldown > 0) {
      cooldownTimer.current = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(cooldownTimer.current);
  }, [resendCooldown]);

  const handleMethodSelect = async (method) => {
    setNotification('');
    setIsError(false);
    setSelectedMethod(method);

    if (method === 'app') {
      if (localHasAuthenticator) {
        setView('enter_code');
      } else {
        setView('setup_authenticator');
      }
      return;
    }

    // For 'email' or 'sms'
    setIsLoading(true);
    try {
      if (method === 'email') {
        const response = await send2faCode({ userId, method: 'email' });
        setResendCooldown(30); // Start cooldown on successful send
        setNotification(response.message);
      } else if (method === 'sms') {
        // Re-create the verifier on demand to ensure it's not stale.
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
        }
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container-2fa', {
          size: 'invisible',
          callback: (response) => console.log('reCAPTCHA solved for 2FA'),
          'error-callback': () => {
            addNotification('reCAPTCHA verification failed. Please try again.', 'error');
            setIsLoading(false); // Ensure loading state is reset
          },
        });
        const result = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
        setConfirmationResult(result);
        setResendCooldown(30); // Start cooldown on successful send
        setNotification('A verification code has been sent to your phone.');
      }
      setView('enter_code'); // Move to code entry on success
    } catch (error) {
      addNotification(`Failed to send code: ${error.message}`, 'error');
      setView('select_method'); // Go back to method selection on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupSuccess = (loginData) => {
    // The setup was successful and the backend logged us in.
    // We just need to pass this success signal up to the Login page.
    onLoginSuccess(loginData);
  };

  const handleSetupCancel = () => {
    setView('select_method');
  };

  const handleResetSuccess = () => {
    setLocalHasAuthenticator(false); // The old authenticator is gone
    setView('setup_authenticator');
  };

  const handleResetCancel = () => {
    setView('enter_code');
    setSelectedMethod('app'); // Ensure we return to the 'app' code entry view
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code || !selectedMethod) {
      setNotification('Please select a method and enter a code.');
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setNotification('');
    setIsError(false);
    try {
      let finalCode = code;
      if (selectedMethod === 'sms' && confirmationResult) {
        if (!confirmationResult) throw new Error('SMS verification not initiated.');
        const credential = await confirmationResult.confirm(code);
        finalCode = await credential.user.getIdToken();
      }

      const loginData = await verify2faCode({ userId, method: selectedMethod, code: finalCode, trustDevice });
      onLoginSuccess(loginData);
    } catch (error) {
      setNotification(error.message);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const maskPhone = (phoneToMask) => {
    if (!phoneToMask || phoneToMask.length < 4) return '';
    // Assuming phone number includes country code, e.g., +15551234567
    return `***-***-${phoneToMask.slice(-4)}`;
  };

  const renderContent = () => {
    switch (view) {
      case 'select_method':
        return (
          <>
            <h3>Two-Factor Authentication</h3>
            <p>For your security, please select a verification method.</p>
            {notification && <div className={`notification ${isError ? 'error' : 'success'}`}>{notification}</div>}
            <div className="method-selection">
              <button onClick={() => handleMethodSelect('app')} className="btn btn-authenticator" disabled={isLoading}>
                <i className="fas fa-shield-alt"></i> By Authenticator App
              </button>
              <button onClick={() => handleMethodSelect('email')} className="btn btn-email" disabled={isLoading || !emailMasked}>
                <i className="fas fa-envelope"></i> By Email {emailMasked && `to ${emailMasked}`}
              </button>
              <button onClick={() => handleMethodSelect('sms')} className="btn btn-sms" disabled={isLoading || !phoneNumber}>
                <i className="fas fa-sms"></i> By SMS {phoneNumber && `to ${maskPhone(phoneNumber)}`}
              </button>
            </div>
          </>
        );
      case 'enter_code':
        return (
          <>
            <h3>Two-Factor Authentication</h3>
            {notification && <div className={`notification ${isError ? 'error' : 'success'}`}>{notification}</div>}
            <form onSubmit={handleSubmit} className="two-factor-form">
              <p className="two-factor-prompt">{selectedMethod === 'app' ? 'Open your authenticator app (e.g., Google Authenticator) and enter the 6-digit code.' : `A code has been sent to your ${selectedMethod}.`}</p>
              <input
                type="text"
                id="2fa-code"
                name="2fa-code"
                className="otp-input-field"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onClick={() => setCode('')}
                placeholder="_ _ _ _ _ _"
                maxLength="6"
                required
                autoFocus
                disabled={isLoading}
              />
              <div className="trust-device-container">
                <input type="checkbox" id="trust-device" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                <label htmlFor="trust-device">Trust this device for 30 days</label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify & Log In'}
                </button>
                <button type="button" onClick={() => setView('select_method')} className="btn btn-secondary" disabled={isLoading}>
                  Change Method
                </button>
              </div>

              {/* Resend and Reset Links */}
              {(selectedMethod === 'email' || selectedMethod === 'sms' || (selectedMethod === 'app' && localHasAuthenticator)) && (
                <div className="forgot-links">
                  {(selectedMethod === 'email' || selectedMethod === 'sms') && (
                    <button type="button" onClick={() => handleMethodSelect(selectedMethod)} className="link-button" disabled={resendCooldown > 0 || isLoading}>
                      {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
                    </button>
                  )}
                  {selectedMethod === 'app' && localHasAuthenticator && (
                    <button type="button" onClick={() => setView('reset_authenticator')} className="link-button" disabled={isLoading}>
                      Lost your device? Reset Authenticator
                    </button>
                  )}
                </div>
              )}
            </form>
          </>
        );
      case 'setup_authenticator':
        return <AuthenticatorSetup setupToken={setupToken} onSuccess={handleSetupSuccess} onCancel={handleSetupCancel} />;
      case 'reset_authenticator':
        return <AuthenticatorReset userId={userId} phoneNumber={phoneNumber} onSuccess={handleResetSuccess} onCancel={handleResetCancel} />;
      default:
        return <p>An unexpected error occurred.</p>;
    }
  };

  return (
    <div className={`two-factor-auth-container ${view === 'setup_authenticator' || view === 'reset_authenticator' ? 'wide-view' : ''}`}>
      {renderContent()}
      <div id="recaptcha-container-2fa"></div>
    </div>
  );
};

export default TwoFactorAuth;
